import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Copy, KeyRound, Pencil, Plus } from "lucide-react";
import { api, TIER_LABELS, type MyAccount, type SqStaff, type Tier } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/toast";
import { Badge } from "@/components/ui/badge";
import { BrandHeading } from "@/components/BrandFrame";
import { SFHead, useSortFilter, type SFCol } from "@/components/SortFilter";
import { cn } from "@/lib/cn";

/* ── 👤 ผู้ใช้ — the BBT user-management standard (Ben 2026-10-15) ───────────────────────────────────────────
   ONE table, two kinds of rows: ทีม BBT = EVERY SQ2.0 login (hub adminUsers: password readable to SQ2.0 managers,
   this-web switch for any admin, reset / + ทีม BBT / บัญชีทั้งระบบ for SQ2.0 managers; role + price tiers live here)
   and ลูกค้า/ตัวแทน = this web's own logins (readable password, reset → <u>1234 + forced change, ✕ delete).
   OFF stays listed: greyed, struck through, at the bottom — never hidden, never deleted. Every column decides sort /
   filter (rule 14). Tap a row → boxed detail (rule 8). */

const ALL_TIERS: Tier[] = ["license", "wholesale", "general"];
/** what an SQ2.0 login with no row here sees on first sign-in (worker DEFAULT_STAFF_TIERS) */
const DEFAULT_STAFF_TIERS: Tier[] = ["license", "wholesale"];
const URLS = ["https://prices.beijer-b-grimm.com", "https://bbt-prices.beijerrefthai.workers.dev"];
const SQ_OA = "@046fxkhr";

/** SQ2.0 teams (quotation_users.team) — same Thai labels on every BBT web */
const TEAM_TH: Record<string, string> = { manager: "ผู้จัดการ", sales: "เซลส์", opd: "OPD", purchase: "จัดซื้อ", marketing: "การตลาด" };
const TEAMS = Object.keys(TEAM_TH);

type Kind = "staff" | "cust";
const KIND_LABEL: Record<Kind, string> = { staff: "ทีม BBT", cust: "ลูกค้า/ตัวแทน" };

type Item = {
  key: string;
  team: string | null;
  email: string | null;
  kind: Kind;
  username: string;
  name: string;
  local: any | null;
  sq: SqStaff | null;
  pw: string | null;
  mustChange: boolean;
  lastMs: number | null;
  webOn: boolean;
  acctOn: boolean;
  tiers: Tier[];
  tiersDefault: boolean;
  role: "admin" | "user";
};
const isOff = (i: Item) => !i.webOn || !i.acctOn;
const statusOf = (i: Item) => (!i.acctOn ? "ปิดบัญชี" : !i.webOn ? (i.kind === "staff" ? "ปิดเว็บนี้" : "ปิด") : "ใช้งาน");

function buildItems(users: any[], staff: SqStaff[] | null): Item[] {
  const byName = new Map(users.map((u) => [String(u.username).toLowerCase(), u]));
  const used = new Set<string>();
  const out: Item[] = [];
  for (const s of staff ?? []) {
    const r = byName.get(s.username.toLowerCase()) ?? null;
    if (r) used.add(r.id);
    out.push({
      key: "sq:" + s.username,
      team: s.team ?? null,
      email: s.email || r?.email || null,
      kind: "staff",
      username: s.username,
      name: (s.fullName || r?.fullName || r?.display_name || "").trim(),
      local: r,
      sq: s,
      pw: s.pw ?? null,
      mustChange: !!s.mustChange,
      lastMs: Math.max(s.lastLogin ?? 0, r?.last_login_at ?? 0) || null,
      webOn: s.appOn !== false && (r ? !!r.is_active : true),
      acctOn: s.active !== false,
      tiers: r ? r.tiers ?? [] : DEFAULT_STAFF_TIERS,
      tiersDefault: !r,
      role: r?.role ?? "user",
    });
  }
  for (const u of users) {
    if (used.has(u.id)) continue;
    const staffRow = u.sq === true; // SQ2.0 down: still a staff row, from the local row
    out.push({
      key: "id:" + u.id,
      team: null,
      email: u.email ?? null,
      kind: staffRow ? "staff" : "cust",
      username: u.username,
      name: (u.fullName || u.display_name || "").trim(),
      local: u,
      sq: null,
      pw: staffRow ? null : u.password_plain ?? null,
      mustChange: !!u.pw_must_change,
      lastMs: u.last_login_at ?? null,
      webOn: !!u.is_active,
      acctOn: !!u.is_active,
      tiers: u.tiers ?? [],
      tiersDefault: false,
      role: u.role,
    });
  }
  return out;
}

/** วิธีการเข้าระบบ — the ONE ready-to-paste LINE message: both URLs, username, password, how-to. */
function loginMsg(i: Pick<Item, "kind" | "username" | "name" | "pw" | "mustChange">) {
  const u = i.username.toLowerCase();
  const pw = i.mustChange ? `${u}1234` : i.pw;
  const lines = [
    `พอร์ทัลราคา BBT — ${i.name || u}`,
    URLS[0],
    `สำรอง: ${URLS[1]}`,
    `ชื่อผู้ใช้: ${u}`,
    `รหัสผ่าน: ${pw ?? (i.kind === "staff" ? "รหัส SQ2.0 ของคุณ (ลืม → แจ้งผู้จัดการ)" : "ตามที่ได้รับ (ลืม → แจ้งแอดมิน)")}`,
  ];
  if (i.mustChange) lines.push("เข้าครั้งแรกระบบให้ตั้งรหัสใหม่ของคุณเอง (6 ตัวขึ้นไป)");
  if (i.kind === "staff") {
    lines.push(`LINE: เพิ่มเพื่อน OA ${SQ_OA} แล้วพิมพ์ ${u} ${pw ?? "<รหัส>"}`);
    lines.push("ชื่อ + รหัสเดียวกัน ใช้ได้ทุกเว็บ BBT");
  } else lines.push("เปิดลิงก์ → ใส่ชื่อผู้ใช้ + รหัสผ่าน → พิมพ์รหัส BJ หรือชื่อสินค้าเพื่อดูราคา");
  return lines.join("\n");
}

const fmt = (ms: number | null | undefined, withTime = true) =>
  ms ? new Date(ms).toLocaleString("th-TH", withTime ? { dateStyle: "short", timeStyle: "short" } : { dateStyle: "short" }) : null;

function Box({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="flex min-h-[40px] items-center gap-2 border-b border-border bg-muted/60 px-3 py-1.5 text-sm font-bold">
        <span>{title}</span>
        <span className="flex-1" />
        {action}
      </div>
      <table className="w-full text-sm">
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
function KV({ k, children }: { k: ReactNode; children: ReactNode }) {
  return (
    <tr className="border-b border-border/60 last:border-0">
      <th className="w-32 bg-muted/30 px-3 py-2 text-left align-middle text-xs font-semibold text-muted-foreground">{k}</th>
      <td className="px-3 py-2 align-middle">{children}</td>
    </tr>
  );
}
const Faint = ({ children = "—" }: { children?: ReactNode }) => <span className="text-muted-foreground/50">{children}</span>;

function OnOff({ on, onClick, onLabel = "เปิด", offLabel = "ปิด", disabled, title }: { on: boolean; onClick: () => void; onLabel?: string; offLabel?: string; disabled?: boolean; title?: string }) {
  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-0.5 text-xs font-medium transition-[filter] duration-300 hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50",
        on ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300",
      )}
    >
      <span className="h-2 w-2 rounded-full bg-current" aria-hidden="true" />
      {on ? onLabel : offLabel}
    </button>
  );
}

function KindPill({ kind }: { kind: Kind }) {
  return kind === "staff" ? (
    <span className="inline-flex items-center whitespace-nowrap rounded-full border border-beijer/40 bg-beijer/10 px-2 py-0.5 text-[11px] font-semibold text-beijer">ทีม BBT</span>
  ) : (
    <span className="inline-flex items-center whitespace-nowrap rounded-full border border-accent-orange/30 bg-accent-orange/15 px-2 py-0.5 text-[11px] font-semibold text-accent-orange">ลูกค้า/ตัวแทน</span>
  );
}

type CustForm = { id?: string; username: string; password: string; display_name: string; company: string; phone: string; email: string; role: "admin" | "user"; tiers: Tier[] };
const emptyCust: CustForm = { username: "", password: "", display_name: "", company: "", phone: "", email: "", role: "user", tiers: ["wholesale"] };

/** 👤 ผู้ใช้ — managers / admins get the whole table; everyone else signed in sees only their own account. */
export default function AdminUsersPage() {
  const { user } = useAuth();
  return user?.role === "admin" || user?.usersAdmin ? <UsersTable /> : <MyAccountView />;
}

function UsersTable() {
  const qc = useQueryClient();
  const { push } = useToast();
  const usersQ = useQuery({ queryKey: ["users"], queryFn: api.listUsers });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState<string | null>(null);
  const [creds, setCreds] = useState<{ title: string; msg: string } | null>(null);
  const [custOpen, setCustOpen] = useState(false);
  const [cust, setCust] = useState<CustForm>(emptyCust);
  const [staffOpen, setStaffOpen] = useState(false);
  const [newStaff, setNewStaff] = useState({ username: "", fullName: "" });

  const me = usersQ.data?.me ?? { sq: null, sqRole: null, canWriteStaff: false };
  const mgr = me.sqRole === "manager";
  const items = useMemo(() => buildItems(usersQ.data?.users ?? [], usersQ.data?.staff ?? null), [usersQ.data]);

  async function run(fn: () => Promise<unknown>, ok = "บันทึกแล้ว") {
    setBusy(true);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ["users"] });
      if (ok) push({ title: ok });
    } catch (e: any) {
      push({ title: "ไม่สำเร็จ", description: e.message, variant: "error" });
    } finally {
      setBusy(false);
    }
  }
  function copy(key: string, msg: string) {
    navigator.clipboard.writeText(msg).then(
      () => {
        setCopied(key);
        setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
      },
      () => window.prompt("คัดลอก", msg),
    );
  }

  // this web on/off — any admin here (staff: SQ2.0 setWeb for 'prices'; customer: its local is_active)
  function setWeb(i: Item, on: boolean) {
    if (!on && !confirm(`ปิดเว็บราคาให้ ${i.username}? จะเข้าไม่ได้จนกว่าจะเปิดใหม่`)) return;
    if (i.kind === "staff" && i.sq) return run(() => api.sqSetWeb(i.username, on));
    if (i.local) return run(() => api.updateUser(i.local.id, { is_active: on }));
  }
  // บัญชีทั้งระบบ — SQ2.0 managers: left BBT → OFF everywhere, never deleted (still a BC365 salesperson)
  function setAcct(i: Item, on: boolean) {
    if (!on && !confirm(`ปิดบัญชี ${i.username} ทั้งระบบ? (ออกจาก BBT) — ทุกเว็บจะเข้าไม่ได้ · ข้อมูล/ประวัติยังอยู่ ไม่ลบ`)) return;
    return run(() => api.sqSetActive(i.username, on));
  }
  function reset(i: Item) {
    const u = i.username.toLowerCase();
    if (!confirm(`รีเซ็ตรหัสของ ${u}?\nรหัสจะเป็น ${u}1234 และเข้าครั้งหน้าต้องตั้งรหัสใหม่`)) return;
    return run(async () => {
      if (i.kind === "staff") await api.sqReset(i.username);
      else await api.updateUser(i.local.id, { resetPw: true });
      setCreds({ title: `รีเซ็ตแล้ว — ${u}`, msg: loginMsg({ ...i, pw: `${u}1234`, mustChange: true }) });
    }, "");
  }
  const canEmail = (i: Item) => (i.kind === "staff" ? !!i.sq && me.canWriteStaff && (mgr || i.username === me.sq) : !!i.local);
  function editEmail(i: Item) {
    const v = window.prompt(`อีเมลของ ${i.username} (เว้นว่าง = ลบ)`, i.email ?? "");
    if (v === null) return;
    if (i.kind === "staff") return run(() => api.sqSetEmail(i.username, v.trim()));
    return run(() => api.updateUser(i.local.id, { email: v.trim() || null }));
  }
  function remove(i: Item) {
    if (!confirm(`ลบบัญชี ${i.username} ถาวร?\nคนที่มีประวัติ แนะนำ "ปิด" แทนการลบ`)) return;
    return run(() => api.deleteUser(i.local.id), "ลบผู้ใช้แล้ว");
  }
  function setTiers(i: Item, tiers: Tier[]) {
    // an SQ2.0 login with no row here yet: the row is made now (role + tiers only, no password)
    if (!i.local) return run(() => api.createUser({ username: i.username, tiers }));
    return run(() => api.updateUser(i.local.id, { tiers }));
  }
  function setRole(i: Item, role: "admin" | "user") {
    if (!i.local) return run(() => api.createUser({ username: i.username, role, tiers: i.tiers }));
    return run(() => api.updateUser(i.local.id, { role }));
  }
  function saveCust() {
    const f = cust;
    if (!f.username.trim()) return push({ title: "ต้องระบุชื่อผู้ใช้", variant: "error" });
    if (f.password && f.password.length < 4) return push({ title: "รหัสผ่านต้องไม่น้อยกว่า 4 ตัว", variant: "error" });
    return run(async () => {
      if (f.id) {
        await api.updateUser(f.id, { password: f.password || undefined, role: f.role, tiers: f.tiers, display_name: f.display_name, company: f.company, phone: f.phone, email: f.email });
        if (f.password) setCreds({ title: `ตั้งรหัสแล้ว — ${f.username}`, msg: loginMsg({ kind: "cust", username: f.username, name: f.display_name, pw: f.password, mustChange: false }) });
      } else {
        const r = await api.createUser({ username: f.username.trim(), password: f.password || undefined, role: f.role, tiers: f.tiers, display_name: f.display_name || undefined, company: f.company || undefined, phone: f.phone || undefined, email: f.email || undefined });
        setCreds({
          title: `สร้างแล้ว — ${f.username.trim()}`,
          msg: loginMsg({ kind: r.sq ? "staff" : "cust", username: f.username.trim(), name: f.display_name, pw: r.password ?? null, mustChange: !!r.mustChange }),
        });
      }
      setCustOpen(false);
    }, "");
  }
  function createStaff() {
    const u = newStaff.username.trim().toLowerCase();
    return run(async () => {
      const r = await api.sqCreate(u, newStaff.fullName || undefined);
      setStaffOpen(false);
      setNewStaff({ username: "", fullName: "" });
      setCreds({ title: `สร้างล็อกอิน SQ2.0 แล้ว — ${r.username}`, msg: loginMsg({ kind: "staff", username: r.username, name: newStaff.fullName, pw: r.pw, mustChange: true }) });
    }, "");
  }

  const tierText = (i: Item) => (i.tiers.length ? i.tiers.map((t) => TIER_LABELS[t]).join(" · ") : "ไม่เห็นราคา");
  const cols = useMemo<SFCol<Item>[]>(
    () => [
      { k: "dot", label: "", className: "w-6" },
      { k: "kind", label: "ประเภท", filter: "pick", val: (i) => KIND_LABEL[i.kind], sort: (i) => KIND_LABEL[i.kind] },
      { k: "user", label: "ชื่อผู้ใช้", filter: "text", val: (i) => i.username, sort: (i) => i.username },
      { k: "name", label: "ชื่อ", filter: "text", val: (i) => `${i.name} ${i.local?.company ?? ""}`, sort: (i) => i.name || "~" },
      { k: "team", label: "ทีม", filter: "pick", val: (i) => (i.team ? TEAM_TH[i.team] ?? i.team : ""), sort: (i) => (i.team ? TEAM_TH[i.team] ?? i.team : "~") },
      { k: "email", label: "อีเมล", filter: "text", val: (i) => i.email ?? "", sort: (i) => i.email || "~" },
      { k: "role", label: "บทบาท", filter: "pick", val: (i) => (i.role === "admin" ? "แอดมิน" : "ผู้ใช้") },
      { k: "tiers", label: "ระดับราคา", filter: "pick", val: (i) => tierText(i) },
      { k: "pw", label: "รหัสผ่าน", filter: "has", val: (i) => i.pw ?? "" },
      { k: "last", label: "เข้าล่าสุด", sort: (i) => i.lastMs ?? 0 },
      { k: "st", label: "สถานะ", filter: "pick", val: (i) => (isOff(i) ? "ปิด" : "ใช้งาน"), opts: [["ใช้งาน", "ใช้งาน"], ["ปิด", "ปิด"]], sort: (i) => statusOf(i) },
      { k: "act", label: "" },
      { k: "open", label: "", className: "w-8" },
    ],
    [],
  );
  const pinOff = useMemo(() => (i: Item) => (isOff(i) ? 1 : 0), []);
  const sf = useSortFilter(cols, items, pinOff);
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();
  const toggleRow = (k: string) =>
    setExpanded((s) => {
      const n = new Set(s);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BrandHeading>จัดการผู้ใช้</BrandHeading>
          <span
            tabIndex={0}
            role="note"
            title={
              "ทีม BBT = ล็อกอิน SQ2.0 ทุกคน · สวิตช์ในแถว = เปิด/ปิดเว็บนี้ · บัญชีทั้งระบบ (ในรายละเอียด) = ผู้จัดการ SQ2.0 เท่านั้น\n" +
              "บทบาท + ระดับราคา ตั้งที่นี่ · ทีมที่ยังไม่เคยเข้า = License + Wholesale (ค่าเริ่มต้น)\n" +
              "ลูกค้า/ตัวแทน = บัญชีของเว็บนี้ · รีเซ็ต = ชื่อ+1234 แล้วระบบให้ตั้งรหัสใหม่ · รหัสที่แอดมินพิมพ์ = ใช้ได้เลย\n" +
              "รหัส \"—\" = มีแต่แบบเข้ารหัส จะเห็นหลังเจ้าของเข้าครั้งถัดไป\n" +
              "ปิด = ขีดฆ่า อยู่ล่างสุด ไม่ลบ · แตะแถวเพื่อดูรายละเอียด"
            }
            aria-label="วิธีอ่านตาราง"
            className="inline-flex h-6 w-6 cursor-help items-center justify-center rounded-full border border-border text-xs font-bold text-muted-foreground transition-colors duration-300 hover:border-beijer hover:text-beijer"
          >
            ?
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {mgr && me.canWriteStaff && (
            <Button variant="outline" onClick={() => setStaffOpen(true)}>
              <Plus className="h-4 w-4" /> ทีม BBT
            </Button>
          )}
          <Button onClick={() => { setCust(emptyCust); setCustOpen(true); }}>
            <Plus className="h-4 w-4" /> ลูกค้า
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span>
          แสดง {sf.rows.length} / {items.length} · ปิด {items.filter(isOff).length}
        </span>
        {usersQ.data && !usersQ.data.sqOk && <span className="font-medium text-destructive">SQ2.0 ติดต่อไม่ได้ — ทีม BBT แสดงจากข้อมูลในเว็บนี้</span>}
        {usersQ.data?.sqOk && !me.canWriteStaff && <span>ทีม BBT: ดูอย่างเดียว (เข้าด้วยล็อกอิน SQ2.0 เพื่อจัดการ)</span>}
        {sf.active && (
          <button type="button" onClick={sf.clear} className="rounded-full border border-accent-orange px-2.5 py-0.5 text-xs font-medium text-accent-orange transition-colors duration-300 hover:bg-accent-orange/10">
            ✕ ล้างตัวกรอง
          </button>
        )}
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <SFHead cols={cols} rows={items} sf={sf} />
            </thead>
            <tbody>
              {usersQ.isError && (
                <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-destructive">โหลดรายชื่อไม่สำเร็จ — {(usersQ.error as any)?.message}</td></tr>
              )}
              {usersQ.isSuccess && sf.rows.length === 0 && (
                <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-muted-foreground">— ไม่พบ —</td></tr>
              )}
              {sf.rows.map((i) => {
                const off = isOff(i);
                const isOpen = expanded.has(i.key);
                const strike = off ? "line-through decoration-1" : "";
                const dot = off ? "bg-red-500" : i.lastMs ? "bg-emerald-500" : "bg-muted-foreground/40";
                const canWeb = i.kind === "staff" ? !!i.sq && me.canWriteStaff : !!i.local;
                const canReset = i.kind === "staff" ? !!i.sq && mgr && me.canWriteStaff : !!i.local;
                return (
                  <Fragment key={i.key}>
                    <tr
                      onClick={() => toggleRow(i.key)}
                      className={cn(
                        "cursor-pointer border-b border-border/50 transition-colors duration-300 hover:bg-muted/40",
                        isOpen && "bg-beijer/5 hover:bg-beijer/10",
                        off && "bg-muted/40 text-muted-foreground",
                      )}
                    >
                      <td className="px-2 py-2"><span className={cn("block h-2.5 w-2.5 rounded-full", dot)} title={statusOf(i)} aria-label={statusOf(i)} /></td>
                      <td className="px-2 py-2"><KindPill kind={i.kind} /></td>
                      <td className={cn("whitespace-nowrap px-2 py-2 font-mono text-xs font-semibold", strike)}>{i.username}</td>
                      <td className={cn("min-w-[8rem] px-2 py-2", strike)}>
                        {i.name || <Faint />}
                        {i.local?.company && <span className="block text-[11px] text-muted-foreground">{i.local.company}</span>}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2" onClick={stop}>
                        {i.kind === "staff" && i.sq && mgr && me.canWriteStaff ? (
                          <select aria-label={`ทีมของ ${i.username}`} value={i.team ?? ""} disabled={busy} onChange={(e) => run(() => api.sqSetTeam(i.username, e.target.value))} className="cursor-pointer rounded-full border border-beijer/40 bg-beijer/10 px-2 py-0.5 text-xs font-semibold text-beijer">
                            {!i.team && <option value="">—</option>}
                            {TEAMS.map((tm) => <option key={tm} value={tm}>{TEAM_TH[tm]}</option>)}
                          </select>
                        ) : i.team ? (
                          <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-semibold">{TEAM_TH[i.team] ?? i.team}</span>
                        ) : <Faint />}
                      </td>
                      <td className="max-w-[14rem] truncate px-2 py-2 text-xs" onClick={stop}>
                        {i.email ? <span title={i.email}>{i.email}</span> : <Faint />}
                        {canEmail(i) && <button type="button" className="ml-1 text-muted-foreground/60 transition-colors duration-300 hover:text-beijer" title="แก้อีเมล" aria-label={`แก้อีเมล ${i.username}`} onClick={() => editEmail(i)}>✎</button>}
                      </td>
                      <td className="px-2 py-2" onClick={stop}>
                        <select
                          aria-label={`บทบาทของ ${i.username}`}
                          value={i.role}
                          disabled={busy}
                          onChange={(e) => setRole(i, e.target.value as "admin" | "user")}
                          className={cn(
                            "cursor-pointer rounded-full border px-2 py-0.5 text-xs font-semibold transition-colors duration-300",
                            i.role === "admin" ? "border-beijer/40 bg-beijer/10 text-beijer" : "border-border bg-muted text-muted-foreground",
                          )}
                        >
                          <option value="user">ผู้ใช้</option>
                          <option value="admin">แอดมิน</option>
                        </select>
                      </td>
                      <td className="px-2 py-2">
                        <div className={cn("flex flex-wrap gap-1", i.tiersDefault && "opacity-60")} title={i.tiersDefault ? "ค่าเริ่มต้นเมื่อเข้าครั้งแรก" : undefined}>
                          {i.tiers.length ? i.tiers.map((t) => <Badge key={t} variant={t}>{TIER_LABELS[t]}</Badge>) : <Faint>ไม่เห็นราคา</Faint>}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-2 py-2" onClick={stop}>
                        {i.pw ? (
                          <span className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">{i.pw}</span>
                        ) : i.kind === "staff" && !mgr ? (
                          <span className="font-mono text-xs text-muted-foreground" title="เฉพาะผู้จัดการ SQ2.0">•••</span>
                        ) : (
                          <span className="text-muted-foreground/60" title="มีแต่แบบเข้ารหัส — จะเห็นหลังเจ้าของเข้าครั้งถัดไป">—</span>
                        )}
                        {i.mustChange && (
                          <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800" title={`ยังใช้ ${i.username.toLowerCase()}1234 — เข้าครั้งหน้าต้องตั้งรหัสใหม่`}>รอตั้งใหม่</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-xs">{i.lastMs ? <span className="text-muted-foreground">{fmt(i.lastMs, false)}</span> : <Faint>ยังไม่เคย</Faint>}</td>
                      <td className="whitespace-nowrap px-2 py-2" onClick={stop}>
                        <OnOff on={i.webOn} disabled={busy || !canWeb} title={i.kind === "staff" ? "เปิด/ปิดเว็บราคาให้คนนี้" : "เปิด/ปิดบัญชีนี้"} onClick={() => setWeb(i, !i.webOn)} />
                        {!i.acctOn && <span className="ml-1 rounded-full bg-red-100 px-1.5 py-0.5 text-[11px] font-semibold text-red-800">ปิดบัญชี</span>}
                        {i.acctOn && !i.webOn && i.kind === "staff" && <span className="ml-1 text-[11px] font-semibold text-red-700">ปิดเว็บนี้</span>}
                      </td>
                      <td className="whitespace-nowrap px-1 py-2 text-right" onClick={stop}>
                        <span className="inline-flex items-center gap-1">
                          {canReset && (
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" disabled={busy} title={`รีเซ็ต → ${i.username.toLowerCase()}1234 + บังคับตั้งใหม่`} onClick={() => reset(i)}>
                              <KeyRound className="h-3.5 w-3.5" /> รีเซ็ต
                            </Button>
                          )}
                          {i.kind === "cust" && i.local && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" disabled={busy} title="ลบบัญชี (คนที่มีประวัติ → ปิดแทน)" aria-label={`ลบ ${i.username}`} onClick={() => remove(i)}>✕</Button>
                          )}
                          <Button variant="outline" size="sm" className="h-8 gap-1.5 whitespace-nowrap px-2.5 text-xs" title="คัดลอกข้อความพร้อมวางใน LINE: ลิงก์ + ชื่อผู้ใช้ + รหัส + วิธีเข้า" onClick={() => copy(i.key, loginMsg(i))}>
                            {copied === i.key ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                            {copied === i.key ? "คัดลอกแล้ว" : "วิธีการเข้าระบบ"}
                          </Button>
                        </span>
                      </td>
                      <td className="px-2 py-2"><ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-300", isOpen && "rotate-180")} aria-hidden="true" /></td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b-2 border-beijer/20 bg-beijer/5">
                        <td colSpan={cols.length} className="px-3 pb-4 pt-2 sm:pl-8">
                          <div className="grid items-start gap-3 md:grid-cols-2">
                            <Box
                              title="บัญชี"
                              action={i.kind === "cust" && i.local ? (
                                <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => { setCust({ id: i.local.id, username: i.username, password: "", display_name: i.local.display_name ?? "", company: i.local.company ?? "", phone: i.local.phone ?? "", email: i.local.email ?? "", role: i.role, tiers: i.tiers }); setCustOpen(true); }}>
                                  <Pencil className="h-3.5 w-3.5" /> แก้ไข
                                </Button>
                              ) : undefined}
                            >
                              <KV k="ประเภท"><KindPill kind={i.kind} /></KV>
                              {i.kind === "staff" && (
                                <KV k="บัญชีทั้งระบบ">
                                  <span className="flex flex-wrap items-center gap-2">
                                    <OnOff on={i.acctOn} onLabel="ใช้งาน" offLabel="ปิดบัญชี" disabled={busy || !i.sq || !mgr || !me.canWriteStaff} title={mgr ? "ออกจาก BBT → ปิดบัญชี (ทุกเว็บ) · ไม่ลบ" : "เฉพาะผู้จัดการ SQ2.0"} onClick={() => setAcct(i, !i.acctOn)} />
                                    {!mgr && <span className="text-xs text-muted-foreground">เฉพาะผู้จัดการ SQ2.0</span>}
                                  </span>
                                </KV>
                              )}
                              <KV k={i.kind === "staff" ? "เว็บนี้" : "บัญชี"}><OnOff on={i.webOn} disabled={busy || !canWeb} onClick={() => setWeb(i, !i.webOn)} /></KV>
                              {i.local?.company && <KV k="บริษัท">{i.local.company}</KV>}
                              {i.local?.phone && <KV k="โทร">{i.local.phone}</KV>}
                              {i.sq?.spCode && <KV k="BC">{i.sq.spCode}</KV>}
                              <KV k="เข้าล่าสุด">{fmt(i.lastMs) ?? <Faint>ยังไม่เคยเข้า</Faint>}</KV>
                              {i.local?.created_at && <KV k="สร้างเมื่อ">{fmt(i.local.created_at, false)}</KV>}
                              {i.sq?.websOff?.length ? <KV k="เว็บที่ปิด">{i.sq.websOff.join(" · ")}</KV> : null}
                            </Box>
                            <div className="flex min-w-0 flex-col gap-3">
                              <Box title="ระดับราคาที่ดูได้">
                                {ALL_TIERS.map((t) => (
                                  <KV key={t} k={<Badge variant={t}>{TIER_LABELS[t]}</Badge>}>
                                    <OnOff on={i.tiers.includes(t)} disabled={busy} onLabel="เห็น" offLabel="ไม่เห็น" onClick={() => setTiers(i, i.tiers.includes(t) ? i.tiers.filter((x) => x !== t) : [...i.tiers, t])} />
                                  </KV>
                                ))}
                              </Box>
                              <Box title="รหัสผ่าน">
                                <KV k="รหัส">{i.pw ? <span className="font-mono text-xs">{i.pw}</span> : i.kind === "staff" && !mgr ? <Faint>เฉพาะผู้จัดการ SQ2.0</Faint> : <Faint>— จะเห็นหลังเข้าครั้งถัดไป</Faint>}</KV>
                                <KV k="สถานะ">{i.mustChange ? <span className="text-xs font-semibold text-amber-700">รอตั้งรหัสใหม่ (ยังใช้ {i.username.toLowerCase()}1234)</span> : <Faint>ตั้งเองแล้ว</Faint>}</KV>
                              </Box>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* + ลูกค้า / แก้ไข — blank password on a new login = <u>1234 + forced change on the login form; a typed one is final */}
      <Dialog open={custOpen} onOpenChange={setCustOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{cust.id ? `แก้ไข: ${cust.username}` : "+ ลูกค้า / ตัวแทน"}</DialogTitle>
            <DialogDescription>{cust.id ? "รหัสที่พิมพ์ = ใช้ได้เลย · เว้นว่าง = ไม่เปลี่ยน" : `เว้นรหัสว่าง = ${cust.username.trim().toLowerCase() || "ชื่อ"}1234 แล้วระบบให้ตั้งรหัสใหม่ตอนเข้าครั้งแรก`}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            {(
              [
                ["username", "ชื่อผู้ใช้"],
                ["display_name", "ชื่อคน"],
                ["company", "บริษัท / ร้าน"],
                ["phone", "โทร"],
                ["email", "อีเมล (ไม่บังคับ)"],
                ["password", cust.id ? "ตั้งรหัสใหม่ (เว้นว่างได้)" : "รหัสผ่าน (เว้นว่างได้)"],
              ] as const
            ).map(([k, label]) => (
              <div key={k}>
                <Label>{label}</Label>
                <Input autoComplete="off" disabled={k === "username" && !!cust.id} className={k === "password" || k === "username" ? "font-mono" : undefined} value={cust[k]} onChange={(e) => setCust((f) => ({ ...f, [k]: e.target.value }))} />
              </div>
            ))}
            <div>
              <Label>บทบาท</Label>
              <div className="mt-1 flex gap-2">
                {(["user", "admin"] as const).map((r) => (
                  <Button key={r} type="button" size="sm" variant={cust.role === r ? "default" : "outline"} onClick={() => setCust((f) => ({ ...f, role: r }))}>{r === "admin" ? "แอดมิน" : "ผู้ใช้"}</Button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <Label>ระดับราคาที่ดูได้</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {ALL_TIERS.map((t) => (
                <label key={t} className="flex items-center gap-2 rounded-md border border-border p-2 transition-colors duration-300 hover:bg-secondary">
                  <Checkbox checked={cust.tiers.includes(t)} onCheckedChange={(c) => setCust((f) => ({ ...f, tiers: c ? [...f.tiers, t] : f.tiers.filter((x) => x !== t) }))} />
                  <Badge variant={t}>{TIER_LABELS[t]}</Badge>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustOpen(false)}>ยกเลิก</Button>
            <Button onClick={saveCust} disabled={busy}>{cust.id ? "บันทึก" : "สร้างบัญชี"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* + ทีม BBT — creates the SQ2.0 login itself (<u>1234 + forced change); SQ2.0 managers only */}
      <Dialog open={staffOpen} onOpenChange={setStaffOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>+ ทีม BBT (ล็อกอิน SQ2.0)</DialogTitle>
            <DialogDescription>รหัสแรก = ชื่อ+1234 · ใช้ได้ทุกเว็บ BBT · ระบบให้ตั้งรหัสใหม่ตอนเข้าครั้งแรก</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>ชื่อผู้ใช้ (ชื่อจริงภาษาอังกฤษ ตัวเล็ก)</Label>
              <Input className="font-mono" autoComplete="off" value={newStaff.username} onChange={(e) => setNewStaff((f) => ({ ...f, username: e.target.value }))} />
            </div>
            <div>
              <Label>ชื่อ-นามสกุล (ไม่บังคับ)</Label>
              <Input value={newStaff.fullName} onChange={(e) => setNewStaff((f) => ({ ...f, fullName: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStaffOpen(false)}>ยกเลิก</Button>
            <Button onClick={createStaff} disabled={busy || !newStaff.username.trim()}>สร้างล็อกอิน</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* credentials once, ready to paste in LINE */}
      <Dialog open={!!creds} onOpenChange={(o) => !o && setCreds(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{creds?.title}</DialogTitle>
          </DialogHeader>
          <div className="whitespace-pre-wrap break-all rounded-lg border border-border bg-muted/40 p-3 text-sm leading-relaxed">{creds?.msg}</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreds(null)}>ปิด</Button>
            <Button className="gap-2" onClick={() => creds && copy("creds", creds.msg)}>
              {copied === "creds" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied === "creds" ? "คัดลอกแล้ว" : "วิธีการเข้าระบบ"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** บัญชีของฉัน — ONLY the signed-in person's own account (ADDENDUM 2): name, team, role + price tiers here, webs
 *  open (read-only), last login, email (editable) and เปลี่ยนรหัสผ่าน (SQ2.0 password for staff, this web's own else). */
function MyAccountView() {
  const [a, setA] = useState<MyAccount | null>(null);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.myAccount().then((r) => setA(r.account)).catch((e: any) => setErr(e.message));
  useEffect(() => { load(); }, []);
  async function savePw() {
    setMsg("");
    if (pw.next.length < 6) return setMsg("รหัสผ่านใหม่ต้องยาว 6 ตัวขึ้นไป");
    if (pw.next !== pw.confirm) return setMsg("รหัสใหม่สองช่องไม่ตรงกัน");
    setBusy(true);
    try {
      await api.changeMyPassword(pw);
      setPw({ current: "", next: "", confirm: "" });
      setMsg("✓ เปลี่ยนรหัสผ่านแล้ว");
    } catch (e: any) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function editEmail() {
    const v = window.prompt("อีเมลของคุณ (เว้นว่าง = ลบ)", a?.email ?? "");
    if (v === null) return;
    try { await api.myEmail(v.trim()); await load(); } catch (e: any) { window.alert(e.message); }
  }
  return (
    <div className="space-y-4">
      <BrandHeading>บัญชีของฉัน</BrandHeading>
      {err && <p className="text-sm text-destructive">{err}</p>}
      {a && (
        <div className="grid items-start gap-3 md:grid-cols-2">
          <Box title={a.username}>
            <KV k="ชื่อ">{a.name || <Faint />}</KV>
            <KV k="ประเภท"><KindPill kind={a.kind} /></KV>
            {a.kind === "staff" && <KV k="ทีม">{a.team ? TEAM_TH[a.team] ?? a.team : <Faint />}</KV>}
            <KV k="บทบาท">{a.role === "admin" ? "แอดมิน" : "ผู้ใช้"}</KV>
            <KV k="ระดับราคา">
              <div className="flex flex-wrap gap-1">{a.tiers.length ? a.tiers.map((t) => <Badge key={t} variant={t}>{TIER_LABELS[t]}</Badge>) : <Faint>ไม่เห็นราคา</Faint>}</div>
            </KV>
            {a.kind === "staff" && <KV k="เว็บที่ปิด">{a.websOff?.length ? a.websOff.join(" · ") : <Faint>ไม่มี — เปิดทุกเว็บ</Faint>}</KV>}
            {a.company && <KV k="บริษัท">{a.company}</KV>}
            <KV k="อีเมล">
              {a.email || <Faint />}
              <button type="button" className="ml-2 text-muted-foreground/70 transition-colors duration-300 hover:text-beijer" title="แก้อีเมล" onClick={editEmail}>✎</button>
            </KV>
            <KV k="เข้าล่าสุด">{fmt(a.lastLoginMs) ?? <Faint />}</KV>
          </Box>
          <Box title="เปลี่ยนรหัสผ่าน">
            {([["current", "รหัสปัจจุบัน"], ["next", "รหัสใหม่ (6 ตัวขึ้นไป)"], ["confirm", "พิมพ์อีกครั้ง"]] as const).map(([k, label]) => (
              <KV key={k} k={label}>
                <Input type="password" autoComplete={k === "current" ? "current-password" : "new-password"} value={pw[k]} onChange={(e) => setPw({ ...pw, [k]: e.target.value })} />
              </KV>
            ))}
            <KV k="">
              <Button size="sm" disabled={busy || !pw.current || !pw.next} onClick={savePw}><KeyRound className="h-3.5 w-3.5" /> บันทึก</Button>
              {msg && <span className="ml-2 text-xs">{msg}</span>}
              {a.kind === "staff" && <span className="block text-xs text-muted-foreground">รหัสเดียวกับ SQ2.0 — เปลี่ยนที่นี่ ใช้ได้ทุกเว็บ BBT</span>}
            </KV>
          </Box>
        </div>
      )}
    </div>
  );
}
